// TEN19 (docs/build-briefs/TEN19-publish-recipes.md; DEC-339, DEC-342 Q6 and Q7, P0.5): the pure decisions behind the
// location's "From <network name>" area in Settings > Recipes, and behind its Map ingredients step. No DOM, no fetch:
// test/ten19-publish-recipes-ui-model.test.ts proves it.
//
// The area lists the recipes the franchise network offers this location, A to Z (DEC-339). Optional: Accept and Decline.
// Required: nothing to accept or decline. Either way a recipe is not a drink until every ingredient is mapped to one of
// the location's OWN inventory items and a price is set; until then the offer shows Map ingredients. A mapped recipe is the
// location's own drink in the Drinks list below (the network cannot edit it; the location turns it off to retire it).

import type { RecipeCatalogReference } from "../../src/domain/networkCatalog.ts";
import { isRecipeCatalogReference } from "./franchisorRecipeModel.ts";
import { formatRecipeQuantity } from "./catalogDisplay.ts";
import { parseDollarsToCentsExact } from "./money.ts";
import { sortByName } from "./nameSort.ts";

export type RecipeOfferStateDto = "pending" | "needs_mapping" | "adopted" | "declined";

export interface RecipeOfferSlotDto {
  catalogRef?: RecipeCatalogReference;
  suggestedItemId?: string | null;
  key?: string;
  position: number;
  name: string;
  category: string;
  amount: string;
  unit: string;
  notes: string | null;
}

export type RecipeOfferSourceDto = "network" | "own" | "shared";

export interface PublishedAddonChoices {
  groups: { key: string; label: string; required: boolean }[];
  options: { key: string; label: string; required: boolean; groupKey: string | null; quantityPerUse?: string | null; unit?: string | null }[];
}
export interface RecipeOfferDto {
  contentRevision?: number;
  addons?: PublishedAddonChoices;
  publicationId: string;
  /** REC7-S1: `network` is the franchisor's; `own` is a recipe this franchisee published to its own locations. */
  source: RecipeOfferSourceDto;
  /** REC7-S2: the sharing franchisee's business name on a `shared` offer, else null. */
  fromName?: string | null;
  name: string;
  category: string;
  /** RECUX2: the franchisor's build instructions, read only here. Absent or empty when the recipe has none. */
  glass?: string | null;
  garnish?: string | null;
  method?: string[];
  adoption: "optional" | "required";
  state: RecipeOfferStateDto;
  decidedAt: string | null;
  drinkId: string | null;
  slots: RecipeOfferSlotDto[];
  locations: { id: string; name: string }[];
}

export interface RecipeOffersResponseDto {
  network: { name: string } | null;
  offers: RecipeOfferDto[];
}

export function isRecipeOffersShape(value: unknown): value is RecipeOffersResponseDto {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const network = v["network"];
  if (network !== null) {
    if (
      typeof network !== "object" ||
      typeof (network as Record<string, unknown>)["name"] !== "string"
    )
      return false;
  }
  const offers = v["offers"];
  if (!Array.isArray(offers)) return false;
  return offers.every((row) => {
    if (typeof row !== "object" || row === null) return false;
    const r = row as Record<string, unknown>;
    return (
      typeof r["publicationId"] === "string" &&
      (r["contentRevision"] === undefined || (typeof r["contentRevision"] === "number" && Number.isSafeInteger(r["contentRevision"]) && r["contentRevision"] >= 0)) &&
      (r["source"] === "network" || r["source"] === "own" || r["source"] === "shared") &&
      (r["fromName"] === undefined ||
        r["fromName"] === null ||
        typeof r["fromName"] === "string") &&
      typeof r["name"] === "string" &&
      typeof r["category"] === "string" &&
      (r["adoption"] === "optional" || r["adoption"] === "required") &&
      (r["state"] === "pending" ||
        r["state"] === "needs_mapping" ||
        r["state"] === "adopted" ||
        r["state"] === "declined") &&
      (r["decidedAt"] === null || typeof r["decidedAt"] === "string") &&
      (r["drinkId"] === null || typeof r["drinkId"] === "string") &&
      Array.isArray(r["slots"]) &&
      (r["slots"] as unknown[]).every((s) => {
        if (typeof s !== "object" || s === null) return false;
        const slot = s as Record<string, unknown>;
        return (
          (slot["catalogRef"] === undefined || isRecipeCatalogReference(slot["catalogRef"])) &&
          (slot["suggestedItemId"] === undefined || slot["suggestedItemId"] === null || typeof slot["suggestedItemId"] === "string") &&
          typeof slot["position"] === "number" &&
          typeof slot["name"] === "string" &&
          typeof slot["category"] === "string" &&
          typeof slot["amount"] === "string" &&
          typeof slot["unit"] === "string"
        );
      }) &&
      Array.isArray(r["locations"]) &&
      (r["locations"] as unknown[]).every((b) => {
        if (typeof b !== "object" || b === null) return false;
        const siteLocation = b as Record<string, unknown>;
        return typeof siteLocation["id"] === "string" && typeof siteLocation["name"] === "string";
      })
    );
  });
}

export function recipeOffersHeading(networkName: string): string {
  return `From ${networkName}`;
}

export const RECIPE_OFFERS_LOAD_FAILED =
  "The network's recipes could not be loaded. Check your connection and reopen this screen.";

export interface StateView {
  label: string;
  tone: "neutral" | "good" | "warn";
}

export function recipeStateView(offer: Pick<RecipeOfferDto, "state" | "adoption">): StateView {
  switch (offer.state) {
    case "pending":
      return { label: "Not answered", tone: "neutral" };
    case "needs_mapping":
      return { label: "Needs mapping", tone: "warn" };
    case "adopted":
      return { label: offer.adoption === "required" ? "Required" : "Accepted", tone: "good" };
    case "declined":
      return { label: "Declined", tone: "warn" };
  }
}

export interface RecipeOfferView {
  contentRevision?: number;
  existingDrink?: boolean;
  addons?: PublishedAddonChoices;
  publicationId: string;
  name: string;
  /** "Classics. Whiskey 2.00 oz, Simple syrup 0.25 oz". */
  detailText: string;
  /** "Location A, Location B". */
  locationsText: string;
  stateLabel: string;
  tone: StateView["tone"];
  canAccept: boolean;
  canDecline: boolean;
  canMap: boolean;
  /** REC7-S2: only a recipe another franchisee shared can be cloned, until it is adopted or cloned. */
  canClone: boolean;
  /** REC7-S2: "From <business name>" on a shared offer, else empty. */
  fromText: string;
  slots: RecipeOfferSlotDto[];
  /** RECUX2: "Glass: Rocks. Garnish: Orange peel." Present only when the recipe carries a glass or a garnish. */
  buildText?: string;
  /** RECUX2: the method steps in order. Present only when the recipe has steps. */
  steps?: string[];
}

/** "Glass: Rocks. Garnish: Orange peel." or "" when neither is set. */
export function buildTextOf(offer: Pick<RecipeOfferDto, "glass" | "garnish">): string {
  const parts: string[] = [];
  if (typeof offer.glass === "string" && offer.glass.trim() !== "") parts.push(`Glass: ${offer.glass.trim()}.`);
  if (typeof offer.garnish === "string" && offer.garnish.trim() !== "") parts.push(`Garnish: ${offer.garnish.trim()}.`);
  return parts.join(" ");
}

export function slotLabel(slot: Pick<RecipeOfferSlotDto, "name" | "amount" | "unit">): string {
  return `${slot.name} ${formatRecipeQuantity(slot.amount)} ${slot.unit}`;
}

export function slotsText(slots: readonly RecipeOfferSlotDto[]): string {
  return [...slots]
    .sort((a, b) => a.position - b.position)
    .map(slotLabel)
    .join(", ");
}

/** The offers A to Z (DEC-339) whatever order the server sent; the input is not mutated. */
export function recipeOfferViews(offers: readonly RecipeOfferDto[]): RecipeOfferView[] {
  return sortByName(offers, (o) => o.name).map((o) => {
    const state = recipeStateView(o);
    const optional = o.adoption === "optional";
    return {
      publicationId: o.publicationId,
      ...(o.contentRevision === undefined ? {} : {contentRevision: o.contentRevision}),
      existingDrink: o.drinkId !== null,
      name: o.name,
      detailText: `${o.category}. ${slotsText(o.slots)}`,
      locationsText: sortByName(
        o.locations.map((b) => b.name),
        (n) => n,
      ).join(", "),
      stateLabel: state.label,
      tone: state.tone,
      canAccept: optional && (o.state === "pending" || o.state === "declined"),
      canDecline: optional && (o.state === "pending" || o.state === "needs_mapping"),
      canMap: o.state === "needs_mapping",
      canClone: o.source === "shared" && o.state !== "adopted",
      fromText: o.source === "shared" && typeof o.fromName === "string" ? `From ${o.fromName}` : "",
      slots: [...o.slots].sort((a, b) => a.position - b.position),
      ...(buildTextOf(o) === "" ? {} : { buildText: buildTextOf(o) }),
      ...(Array.isArray(o.method) && o.method.length > 0 ? { steps: [...o.method] } : {}),
      ...(o.addons !== undefined && o.addons.options.length > 0 ? { addons: o.addons } : {}),
    };
  });
}

/** The offers of one source: the franchisor's own network ones, or the ones this franchisee published (REC7-S1). */
export function offersFrom(
  response: RecipeOffersResponseDto | null,
  source: RecipeOfferSourceDto,
): RecipeOfferDto[] {
  return response === null ? [] : response.offers.filter((o) => o.source === source);
}

export function showsRecipeOffers(
  response: RecipeOffersResponseDto | null,
  source: RecipeOfferSourceDto = "network",
): boolean {
  if (response === null || offersFrom(response, source).length === 0) return false;
  return source === "own" || response.network !== null;
}

/** The heading over the recipes other franchisees shared with this one (REC7-S2). */
export const SHARED_RECIPE_OFFERS_HEADING = "Shared with you";

/** The heading over the recipes this franchisee published itself. */
export const OWN_RECIPE_OFFERS_HEADING = "Published by you";

export function acceptedMessage(name: string): string {
  return `${name} accepted.`;
}
export function declinedMessage(name: string): string {
  return `${name} declined.`;
}
export function mappedMessage(name: string): string {
  return `${name} added to your menu.`;
}
export function clonedMessage(name: string): string {
  return `${name} cloned to your menu.`;
}

// ---- Map ingredients ---------------------------------------------------------------------------------------------

export interface ItemChoice {
  addonEnabled?: boolean;
  id: string;
  name: string;
}

/** The location's own items for the pickers, A to Z (DEC-339). */
export function itemChoices(items: readonly ItemChoice[]): ItemChoice[] {
  return sortByName(items, (i) => i.name);
}

export interface MapDraft {
  /** slot position -> the location's item id; absent until picked. */
  selected: Record<number, string>;
  priceText: string;
}

export function emptyMapDraft(): MapDraft {
  return { selected: {}, priceText: "" };
}

/** MAPCREATE1 (DEC-594): the select value that means "make a new inventory item from this ingredient's name". Never the default. */
export const CREATE_CHOICE = "__create__";
export const CREATE_CHOICE_LABEL = "Create it";

export const MAP_ITEMS_MESSAGE = "Choose an item for every ingredient.";
export const MAP_PRICE_MESSAGE = "Enter a price above $0.00, for example 14 or 14.50.";

export type MapResult =
  | {
      ok: true;
      body: {
        priceCents?: number;
        mappings: { position: number; liquorProductId: string }[];
        /** MAPCREATE1: the slot positions set to "Create it". Present only when there are some. */
        createSlots?: number[];
      };
    }
  | { ok: false; field: "item" | "price"; message: string; position?: number };

/** The request body for a mapping, or the first problem. The server checks everything again. */
export function mapDraftToBody(slots: readonly RecipeOfferSlotDto[], draft: MapDraft, preserveExistingPrice = false): MapResult {
  const mappings: { position: number; liquorProductId: string }[] = [];
  const createSlots: number[] = [];
  for (const slot of [...slots].sort((a, b) => a.position - b.position)) {
    const picked = draft.selected[slot.position];
    if (picked === undefined || picked === "")
      return { ok: false, field: "item", message: MAP_ITEMS_MESSAGE, position: slot.position };
    if (picked === CREATE_CHOICE) createSlots.push(slot.position);
    else mappings.push({ position: slot.position, liquorProductId: picked });
  }
  const created = createSlots.length > 0 ? { createSlots } : {};
  if (preserveExistingPrice) return {ok: true, body: {mappings, ...created}};
  let cents: number;
  try {
    cents = parseDollarsToCentsExact(draft.priceText.trim().replace(/^\$/, ""), "Price");
  } catch (error) {
    if (error instanceof RangeError)
      return { ok: false, field: "price", message: MAP_PRICE_MESSAGE };
    throw error;
  }
  if (cents < 1) return { ok: false, field: "price", message: MAP_PRICE_MESSAGE };
  return { ok: true, body: { priceCents: cents, mappings, ...created } };
}

/**
 * MAPCREATE1: the items the person set to "Create it", one per distinct ingredient name in recipe order, for the approval list.
 * A catalog-linked ingredient is never offered "Create it" (it must be a confirmed catalog item), so it never appears here.
 */
export function createCandidatesOf(
  slots: readonly RecipeOfferSlotDto[],
  draft: MapDraft,
): { key: string; name: string }[] {
  const names: string[] = [];
  for (const slot of [...slots].sort((a, b) => a.position - b.position)) {
    if (draft.selected[slot.position] === CREATE_CHOICE && !names.includes(slot.name)) names.push(slot.name);
  }
  return names.map((name) => ({ key: name, name }));
}

/** Whether an ingredient's select offers "Create it": not on a catalog-linked ingredient, and not when cloning. */
export function offersCreate(slot: Pick<RecipeOfferSlotDto, "catalogRef">, cloning: boolean): boolean {
  return !cloning && slot.catalogRef === undefined;
}

export const CREATE_BLOCKED_MESSAGE =
  "A recipe is added to your menu only when every ingredient is matched. Tick every item to add it, or go Back and choose one of your own items.";
export const CREATE_CONFIRM_INTRO =
  "They are not in your inventory yet. Tick each one you want added. You fill in size, cost and vendor later.";

export const RECIPE_OFFER_FAILED_MESSAGE =
  "That did not go through. Nothing was changed. Try again.";

/** Words for a failed accept, decline or map. A name clash, a withdrawal and a mapping problem carry the server's own plain sentence. */
export function describeRecipeOfferFailure(
  status: number,
  body: { error?: string; message?: string } | null,
): string {
  if (status === 0) return "Could not reach the server. Check your connection and try again.";
  if (status === 403) return "Your role does not allow changing recipes.";
  if (status === 404) return "That recipe is no longer offered to you. Reopen this screen.";
  if (
    body !== null &&
    typeof body.message === "string" &&
    body.message !== "" &&
    (body.error === "duplicate_active_name" ||
      body.error === "publication_withdrawn" ||
      body.error === "recipe_clone_not_allowed" ||
      body.error === "publication_already_adopted" ||
      body.error === "publication_required" ||
      body.error === "recipe_not_accepted" ||
      body.error === "invalid_request")
  ) {
    return body.message.charAt(0).toUpperCase() + body.message.slice(1);
  }
  return RECIPE_OFFER_FAILED_MESSAGE;
}
